import { useState, useEffect, useCallback, useMemo } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import gqlTag from 'graphql-tag';
import * as mutations from '../../graphql/mutations';
import {
  getLocalDateTime,
  newLocalToISODateFormat,
} from '../../application/common/Utilities';

const gql: any = gqlTag;

declare const global: {
  me: {
    id: string;
    orgID: string;
    isAdmin: boolean;
    name: string;
  };
};

// ── Types ───────────────────────────────────────────────────

export interface FlexFieldConfig {
  name: string;
  label: string;
  type: string;
  required?: boolean;
  requiredDispositions?: string[];
  displayField?: boolean;
  heading?: string;
  default?: string;
  defaultValue?: string;
  values?: { id: string; label?: string }[];
  maxLength?: number;
  minLength?: number;
  minValue?: string;
  maxValue?: string;
  hideLabel?: boolean;
  contactFields?: Record<string, any>;
  suggestedImageCount?: number;
  minRequired?: number;
  minRequiredErrorText?: string;
}

export interface DealTypeConfig {
  dealMappingsName: string;
  dealMappingsDescription: string;
  dealMappingsValue?: string;
  flexFields: FlexFieldConfig[];
  label?: string;
  displayFields?: string[];
  pickerDisplayFields?: string[];
}

export interface DealFormValues {
  id: string;
  name: string;
  description: string;
  notes: string;
  value: string;
  imageName: string;
  imageType: string;
  status: string;
  orgID: string;
  categoryID: string;
  currentDispositionID: string;
  currentOwnerID: string;
  flexFieldData: Record<string, any>;
  [key: string]: any;
}

interface UseDealFormOptions {
  dealId?: string;
  categoryId: string;
  config: DealTypeConfig | null;
}

// ── Queries ─────────────────────────────────────────────────

const getDealByIDQuery = gql`
  query MyQuery($dealID: ID!) {
    getDeal(id: $dealID) {
      id
      name
      notes
      status
      imageName
      description
      flexAttributes
      categoryID
      value
      currentDispositionID
      currentOwnerID
      ownerHistory(
        filter: { isActive: { eq: true }, isDeleted: { eq: false } }
      ) {
        items {
          ownerType
          user {
            id
            name
          }
        }
      }
      pinAddress {
        name
        address
        city
        region
        postalCode
      }
    }
  }
`;

const getDefaultStageQuery = gql`
  query MyQuery($categoryID: ID!) {
    listStageByCategorySequence(
      categoryID: $categoryID
      filter: { isMapDefault: { eq: true } }
      limit: 10000
    ) {
      items {
        id
        sequence
      }
    }
  }
`;

const getDefaultDispositionQuery = gql`
  query MyQuery($categoryID: ID!) {
    listStageDispositionByCategoryID(
      categoryID: $categoryID
      filter: { isDeleted: { eq: false } }
    ) {
      items {
        id
        isDefault
        title
      }
    }
  }
`;

// ── Utilities ───────────────────────────────────────────────

const getNestedValue = (obj: any, path: string): any => {
  if (!obj || !path) return undefined;
  const parts = path.split('.');
  let value = obj;
  for (const part of parts) {
    if (value === null || value === undefined) return undefined;
    value = value[part];
  }
  return value;
};

function newEmptyDeal(): DealFormValues {
  return {
    id: '',
    name: '',
    description: '',
    notes: '',
    value: '',
    imageName: '',
    imageType: '',
    status: 'active',
    orgID: global.me.orgID,
    categoryID: '',
    currentDispositionID: '',
    currentOwnerID: global.me.id,
    flexFieldData: {},
  };
}

// ── Hook ────────────────────────────────────────────────────

export function useDealForm({
  dealId,
  categoryId,
  config,
}: UseDealFormOptions) {
  const [initialValues, setInitialValues] = useState<DealFormValues>(
    newEmptyDeal()
  );
  const [isFormLoading, setIsFormLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [defaultDispositionID, setDefaultDispositionID] = useState<string>('');

  // Fetch default disposition for the category
  const fetchDefaultDisposition = useCallback(async (catId: string) => {
    try {
      const res: any = await API.graphql({
        query: getDefaultDispositionQuery,
        variables: { categoryID: catId },
      });
      const dispositions = res.data.listStageDispositionByCategoryID.items;
      let defaultId = '';
      let defaultDefined = false;
      for (const disposition of dispositions) {
        if (disposition.isDefault) {
          defaultId = disposition.id;
          defaultDefined = true;
        }
        if (
          !defaultDefined &&
          disposition.title.toString().toLowerCase().trim() === 'interested'
        ) {
          defaultId = disposition.id;
        }
      }
      return defaultId;
    } catch (err) {
      console.error('Error fetching default disposition:', err);
      return '';
    }
  }, []);

  // Fetch existing deal for editing
  const fetchDeal = useCallback(
    async (id: string) => {
      try {
        const res: any = await API.graphql({
          query: getDealByIDQuery,
          variables: { dealID: id },
        });
        const deal = res.data.getDeal;
        if (!deal) return null;

        const flexFieldData = deal.flexAttributes
          ? JSON.parse(deal.flexAttributes)
          : {};

        return {
          id: deal.id,
          name: deal.name || '',
          description: deal.description || '',
          notes: deal.notes || '',
          value: deal.value ? deal.value.toString() : '',
          imageName: deal.imageName || '',
          imageType: deal.imageType || '',
          status: deal.status || 'active',
          orgID: global.me.orgID,
          categoryID: deal.categoryID || categoryId,
          currentDispositionID: deal.currentDispositionID || '',
          currentOwnerID: deal.currentOwnerID || global.me.id,
          flexFieldData,
        } as DealFormValues;
      } catch (err) {
        console.error('Error fetching deal:', err);
        return null;
      }
    },
    [categoryId]
  );

  // Initialize form
  useEffect(() => {
    if (!categoryId || !config) return;

    async function init() {
      setIsFormLoading(true);

      const dispositionId = await fetchDefaultDisposition(categoryId);
      setDefaultDispositionID(dispositionId);

      if (dealId) {
        const deal = await fetchDeal(dealId);
        if (deal) {
          if (!deal.currentDispositionID) {
            deal.currentDispositionID = dispositionId;
          }
          setInitialValues(deal);
        }
      } else {
        const empty = newEmptyDeal();
        empty.categoryID = categoryId;
        empty.currentDispositionID = dispositionId;
        setInitialValues(empty);
      }

      setIsFormLoading(false);
    }

    init();
  }, [dealId, categoryId, config, fetchDefaultDisposition, fetchDeal]);

  // Save deal (create or update)
  const saveDeal = useCallback(
    async (
      values: DealFormValues
    ): Promise<{ success: boolean; dealId?: string; error?: string }> => {
      if (!config || !categoryId) {
        return { success: false, error: 'Missing configuration' };
      }

      setIsSaving(true);

      try {
        const flexFieldData = values.flexFieldData || {};

        // Get default stage for new deals
        let stageId = '';
        if (!values.id) {
          const stageRes: any = await API.graphql({
            query: getDefaultStageQuery,
            variables: { categoryID: categoryId },
          });
          const stages = stageRes.data.listStageByCategorySequence.items;
          const sorted = stages.sort(
            (a: any, b: any) => a.sequence - b.sequence
          );
          if (sorted.length > 0) {
            stageId = sorted[0].id;
          } else {
            setIsSaving(false);
            return {
              success: false,
              error:
                'Your org needs a default deal stage to create a deal. Please contact support.',
            };
          }
        }

        // Build the deal object
        const ourDeal: any = {
          name: '',
          notes: values.notes || '',
          status: values.status || 'active',
          imageName: values.imageName || '',
          imageType: values.imageType || '',
          currentOwnerID: values.currentOwnerID || global.me.id,
          currentOwnerType: 'user',
          orgID: global.me.orgID,
          isDeleted: false,
          flexAttributes: JSON.stringify(flexFieldData),
          categoryID: categoryId,
          currentDispositionID:
            values.currentDispositionID || defaultDispositionID,
          tmst: newLocalToISODateFormat(getLocalDateTime()),
        };

        if (!values.id) {
          ourDeal.currentStageID = stageId;
        }

        // Map deal name from flex fields
        if (config.dealMappingsName && config.dealMappingsName !== '') {
          const fields = config.dealMappingsName.split(',');
          for (const field of fields) {
            const fieldPath = field.trim();
            const value = getNestedValue(flexFieldData, fieldPath);
            if (value) {
              if (ourDeal.name) {
                ourDeal.name += ' ';
              }
              ourDeal.name += value;
            }
          }
        }

        // Map deal description from flex fields
        if (
          config.dealMappingsDescription &&
          config.dealMappingsDescription !== ''
        ) {
          const descValue = getNestedValue(
            flexFieldData,
            config.dealMappingsDescription
          );
          if (descValue) {
            ourDeal.description = descValue;
          }
        }

        // Map deal value from flex fields
        if (config.dealMappingsValue && config.dealMappingsValue !== '') {
          if (config.dealMappingsValue in flexFieldData) {
            ourDeal.value = flexFieldData[config.dealMappingsValue];
          }
        }

        // Set ID for updates
        if (values.id) {
          ourDeal.id = values.id;
        }

        const result: any = await API.graphql(
          graphqlOperation(mutations.maintainDealV3, {
            dealInfo: JSON.stringify(ourDeal),
          })
        );

        const returnedId = JSON.parse(result.data.maintainDealV3);
        setIsSaving(false);

        return {
          success: true,
          dealId: returnedId || values.id || '',
        };
      } catch (err: any) {
        console.error('Error saving deal:', err);
        setIsSaving(false);
        return {
          success: false,
          error: err.message || 'There was an error saving your deal.',
        };
      }
    },
    [config, categoryId, defaultDispositionID]
  );

  // Flex fields config from the deal type config
  const flexFieldsConfig = useMemo(() => {
    if (!config?.flexFields) return [];
    return config.flexFields.filter((f) => f.displayField !== false);
  }, [config]);

  return {
    initialValues,
    isFormLoading,
    isSaving,
    flexFieldsConfig,
    saveDeal,
  };
}
